import { apiClient } from "./client";

export type PractitionerStat = {
  practitioner_id: string;
  display_name: string;
  hours: number;
  weighted_hours: number;
};

export type ExerciseModeStat = {
  exercise_mode_id: string;
  code: string;
  label: string;
  color: string | null;
  hours: number;
  location_kind: "ONSITE" | "REMOTE" | "MOBILE" | "MIXED";
};

export type KindStat = {
  kind: string;
  hours: number;
};

export type MonthlyKPI = {
  organization_id: string;
  year: number;
  month: number;
  planning_status: string;
  days_in_month: number;
  active_slot_templates: number;
  total_slots: number;
  filled_slots: number;
  coverage_rate: number;
  gap_days: number;
  // Headcount-aware coverage — honours slot_template.target_headcount.
  required_headcount: number;
  assigned_headcount: number;
  missing_headcount: number;
  cells_required: number;
  cells_empty: number;
  cells_partial: number;
  headcount_coverage_rate: number;
  /** Théorique — jours d'ouverture selon le calendrier de l'organisation. */
  operating_days: number;
  /** Effectif — jours où au moins un praticien a travaillé. */
  regulation_days: number;
  contributors: number;
  hours_onsite: number;
  hours_remote: number;
  remote_rate: number | null;
  hours_contract: number;
  hours_liberal: number;
  liberal_rate: number | null;
  by_practitioner: PractitionerStat[];
  by_exercise_mode: ExerciseModeStat[];
  by_membership_kind: KindStat[];
  desiderata_wishes_total: number;
  desiderata_wishes_satisfied: number;
  desiderata_wish_satisfaction_rate: number | null;
  desiderata_avoids_total: number;
  desiderata_avoids_violated: number;
  desiderata_avoid_respect_rate: number | null;
  gini_weighted_hours: number;
};

export type YearlyMonthEntry = {
  year: number;
  month: number;
  coverage_rate: number;
  filled_slots: number;
  total_slots: number;
  gini_weighted_hours: number;
  wish_satisfaction_rate: number | null;
  gap_days: number;
};

export type YearlyOverview = {
  organization_id: string;
  year: number;
  months: YearlyMonthEntry[];
};

export async function getMonthlyKPI(
  organizationId: string,
  year: number,
  month: number,
): Promise<MonthlyKPI> {
  const { data } = await apiClient.get<MonthlyKPI>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/kpi`,
  );
  return data;
}

export async function getYearlyOverview(
  organizationId: string,
  year: number,
): Promise<YearlyOverview> {
  const { data } = await apiClient.get<YearlyOverview>(
    `/api/v1/organizations/${organizationId}/kpi/yearly/${year}`,
  );
  return data;
}


export type HeatmapSlot = {
  id: string;
  label: string;
  start_time: string;
  end_time: string;
  is_night: boolean;
  is_overnight: boolean;
  target_headcount?: number;
};

export type HeatmapRow = {
  date: string;
  counts: Record<string, number>;
};

export type CoverageHeatmap = {
  organization_id: string;
  from_date: string;
  to_date: string;
  slot_templates: HeatmapSlot[];
  rows: HeatmapRow[];
};

export async function getCoverageHeatmap(
  organizationId: string,
  fromDate: string,
  toDate: string,
): Promise<CoverageHeatmap> {
  const { data } = await apiClient.get<CoverageHeatmap>(
    `/api/v1/organizations/${organizationId}/coverage-heatmap`,
    { params: { from: fromDate, to: toDate } },
  );
  return data;
}


// ---------------------------------------------------------------------------
// Coverage matrix — days × 4h bands
// ---------------------------------------------------------------------------
export type MatrixCell = {
  band: number;
  required: number;
  assigned: number;
  ratio: number | null;
};

export type MatrixRow = {
  date: string;
  cells: MatrixCell[];
};

export type CoverageMatrix = {
  organization_id: string;
  year: number;
  month: number;
  band_hours: number;
  band_labels: string[];
  rows: MatrixRow[];
  worst_bands: string[];
};

export async function getCoverageMatrix(
  organizationId: string,
  year: number,
  month: number,
): Promise<CoverageMatrix> {
  const { data } = await apiClient.get<CoverageMatrix>(
    `/api/v1/organizations/${organizationId}/coverage-matrix`,
    { params: { year, month } },
  );
  return data;
}
